<template>
	<article class="d-employee-page">
		<EmployeePageHeader :intro="intro" :details="details" />
		<ScopedColorTheme theme="blue">
			<LongReadController
				id="long-read-controller"
				:key="$route.path"
				v-slot="longReadController"
				class="d-employee-page__content"
			>
				<!-- CONTROL BAR -->
				<div
					class="
						d-employee-page__control-bar
						sticky
						w-full
						bg-white
						py-2xs/v
						px-layout-margin
						flex flex-wrap
						justify-between
						items-center
						gap-32
					"
				>
					<!-- BUTTON TYPE -->
					<div
						v-if="
							publications &&
							publications.labels &&
							(publications.labels.publications ||
								publications.labels.profile)
						"
						class="flex flex-shrink-0 relative"
					>
						<button
							v-if="
								publications &&
								publications.labels &&
								publications.labels.publications
							"
							class="d-employee-page__control-bar-button mr-12"
							:class="
								buttonShown === 'publications'
									? 'bg-theme text-white flex-col'
									: 'bg-transparent border border-theme text-theme hover:bg-theme-20 cursor-pointer'
							"
							@click="toggleButtons('publications')"
						>
							{{ publications.labels.publications }}
						</button>
						<button
							v-if="
								publications &&
								publications.labels &&
								publications.labels.profile
							"
							class="d-employee-page__control-bar-button"
							:class="
								buttonShown === 'profile'
									? 'bg-theme text-white flex-col'
									: 'bg-transparent border border-theme text-theme hover:bg-theme-20 cursor-pointer'
							"
							@click="toggleButtons('profile')"
						>
							{{ publications.labels.profile }}
						</button>
					</div>
					<!-- PUBLICATION GROUPS TOC links -->
					<SearchListLayoutToc
						v-if="buttonShown === 'publications'"
						:key="$route.path"
						class="
							relative
							w-0
							flex-grow
							<768:hidden
							<1024:order-2 <1024:w-full
							>=1024:w-6/12col
						"
						v-bind="longReadController"
					>
						<div
							class="w-layout-margin flex-shrink-0 >=1024:hidden"
						></div>
					</SearchListLayoutToc>
					<!-- LAYOUT PICKER -->
					<div
						v-if="buttonShown === 'publications'"
						class="
							relative
							flex
							w-1/12col
							min-w-fit
							flex-shrink-0
							items-center
							gap-4xs/h
						"
					>
						<BaseButton
							tag="label"
							aria-label="Skift til flisevisning"
							class="
								relative
								w-40
								>=768:w-44
								h-40
								>=768:h-44
								p-0
								border border-theme
							"
							:class="{
								'bg-transparent hover:bg-theme hover:bg-opacity-10 text-theme opacity-50':
									pickedLayout !== 'grid',
							}"
						>
							<input
								v-model="pickedLayout"
								type="radio"
								name="layout-switch-1"
								class="
									appearance-none
									absolute
									left-0
									top-0
									w-full
									h-full
								"
								value="grid"
							/>
							<SvgIconGrid class="w-14 h-14" />
						</BaseButton>

						<BaseButton
							tag="label"
							aria-label="Skift til listevisning"
							class="
								relative
								w-40
								>=768:w-44
								h-40
								>=768:h-44
								p-0
								border border-theme
							"
							:class="{
								'bg-transparent hover:bg-theme hover:bg-opacity-10 text-theme opacity-50':
									pickedLayout !== 'list',
							}"
						>
							<input
								v-model="pickedLayout"
								type="radio"
								name="layout-switch-1"
								class="
									appearance-none
									absolute
									left-0
									top-0
									w-full
									h-full
								"
								value="list"
							/>
							<SvgIconList class="w-18 h-14" />
						</BaseButton>
					</div>
				</div>
				<!-- PROFILE LAYOUT -->
				<div
					v-show="buttonShown === 'profile'"
					class=">=1024:ml-4/12col p-layout-margin"
				>
					<div
						v-if="intro && intro.teaser"
						class="text-red mb-lg/v text-sub-teaser font-light"
					>
						{{ intro.teaser }}
					</div>
					<BlockList
						v-if="blocks && blocks.length"
						:block-list="blocks"
						class="col-span-full text-text"
					/>
					<!-- KNOWLEDGE BOXES -->
					<KnowledgeAreaBox
						v-if="
							details && details.areas && details.areas.length > 0
						"
						:items="details.areas"
						:title="details.areasTitle"
						class="w-full mt-xl/v"
					/>
				</div>
				<!-- PUBLICATION LAYOUT -->
				<div
					v-show="buttonShown === 'publications'"
					class="w-full p-layout-margin pt-64"
				>
					<!-- PUBLICATION LIST -->
					<div v-if="pickedLayout === 'list'">
						<div
							v-for="(group, index) in groups"
							:id="`employee-page-publication-${$vnode.key}-${group.id}`"
							:key="index"
							class="mb-3xl/v"
						>
							<LongReadTarget
								class="relative"
								:link-to-toc="{
									name: `Spring til &quot;${group.name}&quot; i indholdsfortegnelsen`,
									attrs: {
										'data-focus-ignore': true,
									},
									setHash: false,
								}"
								:container-id="`employee-page-publication-${$vnode.key}-${group.id}`"
								:title="group.name"
								:meta-data="{
									total: group.total || 0,
								}"
							/>
							<BaseH3 class="font-light mb-2xs/v">{{
								group.name
							}}</BaseH3>
							<ListItem
								v-for="item in group.items"
								:key="item.key || item.id"
								:heading-level-offset="group.name ? 2 : 1"
								v-bind="item"
								class="mt-3xs/v"
							/>
						</div>
					</div>
					<!-- PUBLICATION GRID -->
					<div v-if="pickedLayout === 'grid'">
						<div
							v-for="(group, index) in groups"
							:id="`employee-page-publication-${$vnode.key}-${group.id}`"
							:key="index"
							class="mb-3xl/v"
						>
							<LongReadTarget
								:link-to-toc="{
									name: `Spring til &quot;${group.name}&quot; i indholdsfortegnelsen`,
									attrs: {
										'data-focus-ignore': true,
									},
								}"
								:container-id="`employee-page-publication-${$vnode.key}-${group.id}`"
								:title="group.name"
								:meta-data="{
									total: group.total || 0,
								}"
							/>
							<BaseH3 class="font-light mb-2xs/v">{{
								group.name
							}}</BaseH3>
							<div
								class="
									grid
									gap-x-sm/h gap-y-md/v
									grid-cols-1
									>=768:grid-cols-2
									>=1024:grid-cols-3
									transition-opacity
									duration-200
								"
							>
								<CardItem
									v-for="item in group.items"
									:key="item.key || item.id"
									:heading-level-offset="group.name ? 2 : 1"
									v-bind="item"
									class="mt-3xs/v"
								/>
							</div>
						</div>
					</div>

					<!-- KNOWLEDGE BOXES -->
					<KnowledgeAreaBox
						v-if="
							details && details.areas && details.areas.length > 0
						"
						:items="details.areas"
						:title="details.areasTitle"
						class="w-full mt-xl/v"
					/>
				</div>
				<!-- PUBLICATIONS -->
				<CardListSection
					v-if="
						highlightedPublications &&
						highlightedPublications.items &&
						highlightedPublications.items.length > 0
					"
					:id="`c-employee-page__highlighted-publications-${$vnode.key}`"
					v-bind="highlightedPublications"
					class="px-layout-margin w-full mt-lg/v"
				/>
				<!-- PROJECTS -->
				<CardListSection
					v-if="
						highlightedProjects &&
						highlightedProjects.items &&
						highlightedProjects.items.length > 0
					"
					:id="`c-employee-page__highlighted-projects-${$vnode.key}`"
					v-bind="highlightedProjects"
					class="px-layout-margin w-full pb-0"
					:class="
						highlightedPublications &&
						highlightedPublications.items &&
						highlightedPublications.items.length > 0
							? 'mt-2xs/v'
							: 'mt-lg/v'
					"
				/>
			</LongReadController>
		</ScopedColorTheme>
	</article>
</template>

<script>
import EmployeePageHeader from '~/components/main/EmployeePageHeader.vue';
import BlockList from '~/components/main/BlockList';
import ScopedColorTheme from '~/components/main/ScopedColorTheme';
import KnowledgeAreaBox from '~/components/main/KnowledgeAreaBox';
import CardListSection from '~/components/main/CardListSection';
import SvgIconGrid from '~/assets/svgs/icon-grid.svg?inline';
import SvgIconList from '~/assets/svgs/icon-list.svg?inline';
import ListItem from '~/components/main/ListItem';
import CardItem from '~/components/main/CardItem';
import {
	LongReadController,
	LongReadTarget,
} from '~/components/shared/LongRead';
import SearchListLayoutToc from '~/components/main/SearchListLayoutToc';
import { cookieInformationData } from '~/components/shared/CookieInformation';

let sessionEmployeeButtonShown = null;
let sessionEmployeeLayout = null;
export default {
	name: 'MainEmployeePage',

	components: {
		EmployeePageHeader,
		BlockList,
		ScopedColorTheme,
		KnowledgeAreaBox,
		CardListSection,
		SvgIconGrid,
		SvgIconList,
		ListItem,
		CardItem,
		LongReadTarget,
		LongReadController,
		SearchListLayoutToc,
	},
	inheritAttrs: false,

	props: {
		id: [Number, String],
		intro: Object,
		details: {
			type: Object,
			default: () => ({}),
		},
		highlightedProjects: {
			type: Object,
			default: () => ({}),
		},
		highlightedPublications: {
			type: Object,
			default: () => ({}),
		},
		publications: {
			type: Object,
			default: () => ({}),
		},
		blocks: {
			type: Array,
			default: () => [],
		},
	},
	data() {
		const defaultButtonShown =
			sessionEmployeeButtonShown ||
			this.$cookie.get('EmployeeButtonShown') ||
			'publications';
		const defaultLayout =
			sessionEmployeeLayout ||
			this.$cookie.get('EmployeeLayout') ||
			'list';
		return {
			buttonShown: defaultButtonShown,
			isLoading: false,
			groups: [],
			pickedLayout: defaultLayout,
		};
	},
	watch: {
		buttonShown(value) {
			if (cookieInformationData?.categories?.functional) {
				this.$cookie.set('EmployeeButtonShown', value);
			}
			sessionEmployeeButtonShown = value;
		},
		pickedLayout(value) {
			if (cookieInformationData?.categories?.functional) {
				this.$cookie.set('EmployeeLayout', value);
			}
			sessionEmployeeLayout = value;
		},
	},
	mounted() {
		if (this.publications) {
			const endpointUrl = new URL(
				this.publications.endpointUrl,
				'https://www.example.com/'
			);
			(this.publications.fields || []).forEach((field) => {
				endpointUrl.searchParams.set(field.name, field.value);
			});

			this.$axios
				.get(
					[endpointUrl.pathname, endpointUrl.search]
						.filter(Boolean)
						.join('')
				)
				.then((res) => {
					this.groups = res.data.groups;
					this.isLoading = false;
				})
				.catch(() => {
					this.isLoading = false;
				});
		}
	},
	methods: {
		toggleButtons(type) {
			if (type === this.buttonShown) {
				return;
			}
			this.buttonShown === 'publications'
				? (this.buttonShown = 'profile')
				: (this.buttonShown = 'publications');
		},
	},
};
</script>

<style lang="postcss">
.d-employee-page:before {
	@apply bg-gray-light;
	content: '';
	display: block;
	position: fixed;
	left: -50vw; /* for some reason blur triggers weird stuff if just 0 0 and 100vw and vh */
	top: 0;
	width: 200vw;
	height: calc(
		100vh + 9999999px
	); /* and for some reason the blur cuts the bottom of the page if just 100vh / treats it as semi-absolute? */
	z-index: -1;
}
.d-employee-page__control-bar {
	box-shadow: 0px 4px 24px 2px rgba(86, 86, 86, 0.12);
	top: var(--site-header-height, 0px);
	min-height: 96px;
	z-index: 4 !important;

	& * {
		scroll-margin-top: var(--site-header-height, 0px);
	}
}
.d-employee-page__control-bar:before {
	@apply absolute bg-white;
	content: '';
	left: calc(var(--margin, 0px) * -1);
	top: 0;
	width: calc(100% + var(--margin, 0px) * 2);
	height: 100%;
	z-index: -1;
}
.d-employee-page__control-bar-button {
	@apply h-32 inline-flex items-center justify-center px-12 py-7 rounded-25 text-body-sm transition-colors duration-200 ease-in-out;
}
.d-employee-page__control-bar-publications-button {
	@apply h-24 w-24 rounded-full bg-gray flex py-4 justify-center items-center;
	font-size: 12px;
}

@screen >=1441 {
	.d-employee-page__control-bar {
		--margin: (
				var(--visual-viewport-width, 100vw) -
					var(--theme-layout-max, 1440px)
			) / 2;
	}
}
</style>
